import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  Slider,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Microsoft as MicrosoftIcon, 
  Link as LinkIcon,
  Science as TestIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  testNotification,
  subscribeToWebPush,
  unsubscribeFromWebPush,
  convertToBackendFormat,
  convertFromBackendFormat,
} from '../../services/notificationPreferences';

const Settings: React.FC = () => {
  const { user, hasMicrosoftAccount, linkMicrosoftAccount } = useAuth();
  const [settings, setSettings] = useState({
    notifications: {
      // Notification channels
      emailNotifications: true,
      pushNotifications: true,
      smsNotifications: false,
      
      // Notification types
      deadlineReminders: true,
      followUpReminders: true,
      classificationComplete: false,
      externalResponseNeeded: true,
      
      // Priority filtering
      minNotificationPriority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
      
      // Quiet hours
      quietHoursEnabled: false,
      quietHoursStart: '22:00',
      quietHoursEnd: '08:00',
      quietHoursTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    processing: {
      summaryThreshold: 500,
      classificationSensitivity: 70,
      reminderInterval: 48,
      followUpInterval: 72,
    },
    privacy: {
      localProcessingOnly: false,
      dataRetentionDays: 30,
      encryptLocalStorage: true,
    },
  });

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const [testingNotification, setTestingNotification] = useState<string | null>(null);

  const handleNotificationChange = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: event.target.checked,
      },
    }));
  };

  const handleNotificationSelectChange = (key: string) => (event: any) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: event.target.value,
      },
    }));
  };

  const handleNotificationTimeChange = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: event.target.value,
      },
    }));
  };

  const handleProcessingChange = (key: string) => (event: Event, value: number | number[]) => {
    setSettings(prev => ({
      ...prev,
      processing: {
        ...prev.processing,
        [key]: value as number,
      },
    }));
  };

  const handlePrivacyChange = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({
      ...prev,
      privacy: {
        ...prev.privacy,
        [key]: event.target.checked,
      },
    }));
  };

  const handleDataRetentionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value) || 30;
    setSettings(prev => ({
      ...prev,
      privacy: {
        ...prev.privacy,
        dataRetentionDays: value,
      },
    }));
  };

  // Load notification preferences on component mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await getNotificationPreferences();
        if (response.success && response.data) {
          const frontendPrefs = convertFromBackendFormat(response.data);
          setSettings(prev => ({
            ...prev,
            notifications: {
              ...prev.notifications,
              ...frontendPrefs,
            },
          }));
        }
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
      } finally {
        setLoadingPreferences(false);
      }
    };

    loadPreferences();
  }, []);

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      const backendPrefs = convertToBackendFormat(settings.notifications);
      const response = await updateNotificationPreferences(backendPrefs);
      
      if (response.success) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
      } else {
        throw new Error(response.error || 'Failed to save preferences');
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const handleTestNotification = async (channel: 'email' | 'push' | 'sms', type: string) => {
    const testKey = `${channel}-${type}`;
    setTestingNotification(testKey);
    
    try {
      const response = await testNotification(channel, type as any);
      if (response.success) {
        // Show success message briefly
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        throw new Error(response.error || 'Test notification failed');
      }
    } catch (error) {
      console.error('Test notification error:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setTestingNotification(null);
    }
  };

  const handleWebPushToggle = async (enabled: boolean) => {
    if (enabled) {
      const response = await subscribeToWebPush();
      if (!response.success) {
        // Revert the toggle if subscription failed
        setSettings(prev => ({
          ...prev,
          notifications: {
            ...prev.notifications,
            pushNotifications: false,
          },
        }));
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
        return;
      }
    } else {
      await unsubscribeFromWebPush();
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      {saveStatus === 'saved' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      {saveStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to save settings. Please try again.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Notification Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notification Preferences
              </Typography>
              
              <Grid container spacing={3}>
                {/* Notification Channels */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                    Notification Channels
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.notifications.emailNotifications}
                            onChange={handleNotificationChange('emailNotifications')}
                          />
                        }
                        label="Email Notifications"
                      />
                      {settings.notifications.emailNotifications && (
                        <Tooltip title="Send test email notification">
                          <IconButton
                            size="small"
                            onClick={() => handleTestNotification('email', 'deadline_reminder')}
                            disabled={testingNotification === 'email-deadline_reminder'}
                          >
                            <TestIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.notifications.pushNotifications}
                            onChange={(e) => {
                              handleNotificationChange('pushNotifications')(e);
                              handleWebPushToggle(e.target.checked);
                            }}
                          />
                        }
                        label="Web Push Notifications"
                      />
                      {settings.notifications.pushNotifications && (
                        <Tooltip title="Send test push notification">
                          <IconButton
                            size="small"
                            onClick={() => handleTestNotification('push', 'deadline_reminder')}
                            disabled={testingNotification === 'push-deadline_reminder'}
                          >
                            <TestIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.notifications.smsNotifications}
                            onChange={handleNotificationChange('smsNotifications')}
                          />
                        }
                        label="SMS Notifications"
                      />
                      {settings.notifications.smsNotifications && (
                        <Tooltip title="Send test SMS notification">
                          <IconButton
                            size="small"
                            onClick={() => handleTestNotification('sms', 'deadline_reminder')}
                            disabled={testingNotification === 'sms-deadline_reminder'}
                          >
                            <TestIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {settings.notifications.smsNotifications && (
                      <FormHelperText>
                        SMS notifications require a verified phone number in your profile.
                      </FormHelperText>
                    )}
                  </Box>
                </Grid>

                {/* Notification Types */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                    Notification Types
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications.deadlineReminders}
                          onChange={handleNotificationChange('deadlineReminders')}
                        />
                      }
                      label="Deadline Reminders"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications.followUpReminders}
                          onChange={handleNotificationChange('followUpReminders')}
                        />
                      }
                      label="Follow-up Reminders"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications.classificationComplete}
                          onChange={handleNotificationChange('classificationComplete')}
                        />
                      }
                      label="Email Classification Complete"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications.externalResponseNeeded}
                          onChange={handleNotificationChange('externalResponseNeeded')}
                        />
                      }
                      label="External Response Needed"
                    />
                  </Box>
                </Grid>

                {/* Priority and Quiet Hours */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                    Priority & Timing
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Minimum Priority</InputLabel>
                      <Select
                        value={settings.notifications.minNotificationPriority}
                        onChange={handleNotificationSelectChange('minNotificationPriority')}
                        label="Minimum Priority"
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="urgent">Urgent Only</MenuItem>
                      </Select>
                      <FormHelperText>
                        Only receive notifications at or above this priority level
                      </FormHelperText>
                    </FormControl>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications.quietHoursEnabled}
                          onChange={handleNotificationChange('quietHoursEnabled')}
                        />
                      }
                      label="Enable Quiet Hours"
                    />

                    {settings.notifications.quietHoursEnabled && (
                      <Box display="flex" flexDirection="column" gap={1}>
                        <TextField
                          size="small"
                          label="Quiet Hours Start"
                          type="time"
                          value={settings.notifications.quietHoursStart}
                          onChange={handleNotificationTimeChange('quietHoursStart')}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          size="small"
                          label="Quiet Hours End"
                          type="time"
                          value={settings.notifications.quietHoursEnd}
                          onChange={handleNotificationTimeChange('quietHoursEnd')}
                          InputLabelProps={{ shrink: true }}
                        />
                        <FormHelperText>
                          No notifications will be sent during quiet hours
                        </FormHelperText>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Processing Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Processing Settings
              </Typography>
              <Box display="flex" flexDirection="column" gap={3}>
                <Box>
                  <Typography gutterBottom>
                    Summary Threshold (words): {settings.processing.summaryThreshold}
                  </Typography>
                  <Slider
                    value={settings.processing.summaryThreshold}
                    onChange={handleProcessingChange('summaryThreshold')}
                    min={100}
                    max={1000}
                    step={50}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Box>
                
                <Box>
                  <Typography gutterBottom>
                    Classification Sensitivity: {settings.processing.classificationSensitivity}%
                  </Typography>
                  <Slider
                    value={settings.processing.classificationSensitivity}
                    onChange={handleProcessingChange('classificationSensitivity')}
                    min={0}
                    max={100}
                    step={5}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Box>
                
                <Box>
                  <Typography gutterBottom>
                    Reminder Interval (hours): {settings.processing.reminderInterval}
                  </Typography>
                  <Slider
                    value={settings.processing.reminderInterval}
                    onChange={handleProcessingChange('reminderInterval')}
                    min={12}
                    max={168}
                    step={12}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Box>
                
                <Box>
                  <Typography gutterBottom>
                    Follow-up Interval (hours): {settings.processing.followUpInterval}
                  </Typography>
                  <Slider
                    value={settings.processing.followUpInterval}
                    onChange={handleProcessingChange('followUpInterval')}
                    min={24}
                    max={168}
                    step={12}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Integration */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Integration
              </Typography>
              
              <Box display="flex" alignItems="center" justifyContent="space-between" py={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <MicrosoftIcon color="primary" />
                  <Box>
                    <Typography variant="body1">Microsoft Outlook</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {hasMicrosoftAccount 
                        ? `Connected as ${user?.microsoftAccount?.username}`
                        : 'Connect your Microsoft account to enable email processing'
                      }
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1}>
                  {hasMicrosoftAccount ? (
                    <Chip
                      icon={<LinkIcon />}
                      label="Connected"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={linkMicrosoftAccount}
                      disabled={saveStatus === 'saving'}
                    >
                      Connect
                    </Button>
                  )}
                </Box>
              </Box>
              
              {hasMicrosoftAccount && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Your Microsoft account is connected. The application can now access your 
                  Outlook emails and calendar to provide intelligent email management features.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Privacy Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Privacy & Security
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.privacy.localProcessingOnly}
                          onChange={handlePrivacyChange('localProcessingOnly')}
                        />
                      }
                      label="Local Processing Only"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.privacy.encryptLocalStorage}
                          onChange={handlePrivacyChange('encryptLocalStorage')}
                        />
                      }
                      label="Encrypt Local Storage"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Data Retention (days)"
                    type="number"
                    value={settings.privacy.dataRetentionDays}
                    onChange={handleDataRetentionChange}
                    inputProps={{ min: 1, max: 365 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
        >
          {saveStatus === 'saving' ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
};

export default Settings;